/**
 * B2B portal — in-page filtering.
 *
 * ONE behaviour, three custom elements. The catalog, orders and resources
 * pages all need "narrow this server-rendered list as I type", so they share
 * a base class rather than each shipping a near-copy that drifts.
 *
 * PROGRESSIVE ENHANCEMENT. Every item is server-rendered and visible before
 * this file runs. The filter UI ships with `hidden` in the markup and is
 * revealed here, so with JavaScript blocked the buyer sees the complete list
 * and no control that does nothing.
 *
 * The filter is presentational only: it hides rows already on the page. It
 * never fetches, never re-prices, and never changes what the server decided
 * the buyer may see.
 *
 * Theme Editor safe: connectedCallback re-runs for injected sections, work is
 * guarded against double-binding, and listeners are bound to the component
 * root and removed on disconnect. No globals, no document-level listeners.
 */
(function () {
  "use strict";

  var ITEM = "[data-b2b-filter-item]";

  class B2BFilterList extends HTMLElement {
    connectedCallback() {
      if (this.dataset.b2bReady === "true") return;
      this.dataset.b2bReady = "true";

      this.input = this.querySelector("[data-b2b-filter-input]");
      this.items = Array.prototype.slice.call(this.querySelectorAll(ITEM));
      if (!this.input || !this.items.length) return;

      this.status = this.querySelector("[data-b2b-filter-status]");
      this.empty = this.querySelector("[data-b2b-filter-empty]");
      this.chips = Array.prototype.slice.call(
        this.querySelectorAll("[data-b2b-filter-chip]")
      );

      // The control is hidden in markup so a no-JS buyer never sees a dead
      // field; revealing it here is the only proof that filtering works.
      var ui = this.querySelectorAll("[data-b2b-filter-ui]");
      Array.prototype.forEach.call(ui, function (el) {
        el.hidden = false;
      });

      this.activeTag = "";
      this.onInput = this.handleInput.bind(this);
      this.onChip = this.handleChip.bind(this);

      this.input.addEventListener("input", this.onInput);
      this.chips.forEach(
        function (chip) {
          chip.addEventListener("click", this.onChip);
        }.bind(this)
      );
    }

    disconnectedCallback() {
      if (this.input && this.onInput) {
        this.input.removeEventListener("input", this.onInput);
      }
      if (this.chips && this.onChip) {
        this.chips.forEach(
          function (chip) {
            chip.removeEventListener("click", this.onChip);
          }.bind(this)
        );
      }
      this.dataset.b2bReady = "false";
    }

    handleChip(event) {
      var chip = event.currentTarget;
      var tag = chip.getAttribute("data-b2b-filter-chip") || "";

      // Clicking the active chip clears it, so a buyer is never trapped in a
      // filtered view with no visible way back to everything.
      this.activeTag = this.activeTag === tag ? "" : tag;

      this.chips.forEach(
        function (other) {
          var on = other.getAttribute("data-b2b-filter-chip") === this.activeTag;
          other.setAttribute("aria-pressed", on ? "true" : "false");
        }.bind(this)
      );

      this.apply();
    }

    handleInput() {
      this.apply();
    }

    apply() {
      var query = (this.input.value || "").trim().toLowerCase();
      var tag = this.activeTag;
      var shown = 0;

      this.items.forEach(function (item) {
        var text = (item.getAttribute("data-b2b-filter-text") || "").toLowerCase();
        var tags = (item.getAttribute("data-b2b-filter-tags") || "").toLowerCase();

        var matchesText = query === "" || text.indexOf(query) !== -1;
        var matchesTag = tag === "" || tags.indexOf(tag.toLowerCase()) !== -1;
        var visible = matchesText && matchesTag;

        item.hidden = !visible;
        if (visible) shown += 1;
      });

      if (this.empty) this.empty.hidden = shown !== 0;
      this.announce(shown);
    }

    /**
     * Screen reader users get no feedback from rows silently disappearing,
     * so the visible count doubles as the live region.
     */
    announce(shown) {
      if (!this.status) return;
      var template = this.status.getAttribute("data-b2b-filter-status") || "";
      this.status.textContent = template
        .replace("{{ shown }}", String(shown))
        .replace("{{ total }}", String(this.items.length));
    }
  }

  ["b2b-catalog-filter", "b2b-order-filters", "b2b-resource-filter"].forEach(
    function (name) {
      if (!customElements.get(name)) {
        customElements.define(name, class extends B2BFilterList {});
      }
    }
  );
})();
